import { ApiError, request } from './client';
import { anonymousId } from '../state/anonymous';
import type { AppPlan } from '@/webapp/plan/planTypes';

/**
 * V7's "Email me when it's ready" (docs/superpowers/specs/2026-10-16-v7-build.md; infra builds the
 * server half). Signed out, while the app builds: the address is kept with the build's anonymous id
 * and key, and the build's finisher sends "Your … is ready" the moment it is done (or "didn't build").
 *
 * `thing` is the noun on the email's button, "Open my <thing>" (journey/demos.ts `emailNoun`).
 * `queued`: it is still building and the email follows. `sent`: it was already built, so it went now.
 * Refusals come as { error, reason }: 400 invalid, 404 no_build, 409 unsubscribed, 429 rate_limited,
 * 502 send_failed, 503 mail_off.
 */
export function requestReadyLink(input: {
  requestKey: string;
  email: string;
  goal: string;
  appName: string;
  thing: string;
  plan?: AppPlan | null;
}): Promise<{ queued: true } | { sent: true }> {
  const { plan, ...rest } = input;
  return request<{ queued: true } | { sent: true }>('/waiting/ready-link', {
    base: 'api',
    method: 'POST',
    body: { anonId: anonymousId(), ...rest, ...(plan ? { plan } : {}) },
  });
}

/** What the email's link opens: the build it is for, and whether the account was made for it. */
export interface ReadyLink {
  email: string;
  anonId: string;
  requestKey: string;
  goal: string;
  appName: string;
  plan?: AppPlan | null;
  /** `gone`: the app was collected already — sign in and go to their apps. */
  build: 'done' | 'running' | 'failed' | 'gone';
  /** The link was opened before: it no longer signs in (one use), so the emailed code does. */
  linkUsed?: boolean;
  /** The server made the account for this address just now (verified): it is asked to finish it. */
  newAccount: boolean;
}

/** Trade the link's token for its build. 400 `bad_link`: bad, used up or past its three days. 502 `account_failed`. */
export function openReadyLink(token: string): Promise<ReadyLink> {
  return request<ReadyLink>('/waiting/ready-open', { base: 'api', method: 'POST', body: { token } });
}

/**
 * "Finish your account": the password of the account the link made (signed in, so the request carries its
 * token). Once, within a day of opening the link (infra shared/readyPassword.ts). Refusals: 400 weak/invalid,
 * 401 signed_out, 403 not_allowed (already set up), 502 failed.
 */
export function setReadyPassword(password: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/waiting/ready-password', { base: 'api', method: 'POST', body: { password } });
}

/** The machine code of a refusal, wherever the server put it. */
const reasonOf = (err: ApiError): string | undefined => err.code ?? err.reason;

/** Why the email could not be asked for, as the screen says it. */
export function readyLinkRefusal(err: unknown): {
  code: 'rate_limited' | 'mail_off' | 'unsubscribed' | 'no_build' | 'invalid' | 'error';
  text: string;
} {
  if (err instanceof ApiError) {
    const reason = reasonOf(err);
    if (err.kind === 'rate_limited' || reason === 'rate_limited') return { code: 'rate_limited', text: 'That’s a lot of tries. Wait a minute, then send it again.' };
    if (err.status === 503 || reason === 'mail_off') {
      return { code: 'mail_off', text: 'We can’t send email just now. Wait here instead: it opens on this page.' };
    }
    if (err.status === 409 || reason === 'unsubscribed') {
      return { code: 'unsubscribed', text: 'That address has asked us not to email it. Wait here instead, or use another.' };
    }
    if (err.status === 404 || reason === 'no_build') return { code: 'no_build', text: 'There’s no app building in this browser to send.' };
    if (err.status === 400 || reason === 'invalid') return { code: 'invalid', text: 'Check the email address.' };
  }
  return { code: 'error', text: 'That didn’t send. Try again in a moment.' };
}
